<template>
  <b-modal
    v-model="show"
    modal-class="modal-primary modal-resumen-bono"
    size="xl"
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    scrollable
    @hidden="$emit('close')"
  >
    <!-- CABECERA PERSONALIZADA CON NOMBRE DEL CONDUCTOR EN GRANDE -->
    <template #modal-title>
      <div class="d-flex align-items-center py-25">
        <div class="avatar bg-light-primary mr-1 p-50 rounded-circle">
          <feather-icon icon="UserIcon" size="28" class="text-primary" />
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder text-primary text-uppercase driver-name-title">
            {{ conductor.nombre_completo || 'CONDUCTOR' }}
          </h2>
          <div class="d-flex align-items-center text-dark font-small-3 mt-25">
            <span class="font-weight-bold">
              DNI: <span class="text-primary font-weight-bolder">{{ conductor.numero_documento }}</span>
            </span>
            <span class="mx-75 text-muted">|</span>
            <span class="text-secondary font-weight-normal">Resumen de Liquidación de Bonos</span>
          </div>
        </div>
      </div>
    </template>

    <!-- SPINNER DE CARGA -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..." />
      <p class="mt-1 text-muted font-small-3 font-weight-bold">Cargando el desglose del bono...</p>
    </div>

    <!-- CONTENIDO PRINCIPAL EN UNA SOLA VISTA -->
    <div v-else class="modal-resumen-body">
      <!-- HEADER CON BADGES DE PERIODO, CATEGORIA Y UT -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-2 pb-1 border-bottom">
        <div class="d-flex flex-wrap align-items-center gap-x-2 gap-y-1">
          <b-badge variant="light-primary" class="font-medium-1 px-1 py-50 font-weight-bolder">
            <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
            {{ conductor.periodo }}
          </b-badge>
          <b-badge variant="light-info" v-if="conductor.categoria && conductor.categoria !== '-'" class="font-medium-1 px-1 py-50 font-weight-bolder">
            Categoría {{ conductor.categoria }}
          </b-badge>
          <b-badge variant="light-secondary" v-if="conductor.tipo_ut && conductor.tipo_ut !== '-'" class="font-medium-1 px-1 py-50 font-weight-bolder">
            UT: {{ conductor.tipo_ut }}
          </b-badge>
          <b-badge
            :variant="conductor.estado === 'FINALIZADO' ? 'light-success' : 'light-warning'"
            class="font-medium-1 px-1 py-50 text-uppercase font-weight-bolder"
          >
            {{ conductor.estado }}
          </b-badge>
        </div>

        <div class="text-dark font-small-3 font-weight-bold mt-1 mt-md-0" v-if="conductor.fecha_inicio && conductor.fecha_fin">
          Período: <span class="text-body-dark">{{ conductor.fecha_inicio | globalDate }}</span> — <span class="text-body-dark">{{ conductor.fecha_fin | globalDate }}</span>
        </div>
      </div>

      <!-- TARJETAS KPI DE RESUMEN SUPERIOR (SOLO CONCEPTOS QUE TIENEN DATOS) -->
      <b-row class="mb-2">
        <!-- 1. Viajes por Distancia -->
        <b-col v-if="viajes.length > 0" class="mb-1" :md="kpiColSpan" sm="6">
          <b-card no-body class="border shadow-none mb-0 bg-light-primary p-1 h-100 card-kpi-custom">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bolder text-primary text-uppercase kpi-title">
                <feather-icon icon="TruckIcon" size="16" class="mr-25" /> Viajes por Distancia
              </span>
              <b-badge variant="primary" class="font-medium-1 px-1 font-weight-bolder">
                {{ totales.cant_viajes }} viajes
              </b-badge>
            </div>
            <div class="d-flex justify-content-between align-items-baseline mt-1 pt-50 border-top-light">
              <span class="text-muted font-small-3 font-weight-bold">Subtotal:</span>
              <span class="font-weight-bolder text-primary kpi-amount">
                S/ {{ totales.monto_viajes | currency }}
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- 2. Cambios de Conductor (Carguíos) -->
        <b-col v-if="cambiosConductor.length > 0" class="mb-1" :md="kpiColSpan" sm="6">
          <b-card no-body class="border shadow-none mb-0 bg-light-warning p-1 h-100 card-kpi-custom">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bolder text-warning text-uppercase kpi-title">
                <feather-icon icon="UsersIcon" size="16" class="mr-25" /> Cambios Conductor (Carguíos)
              </span>
              <b-badge variant="warning" class="font-medium-1 px-1 font-weight-bolder">
                {{ totales.cant_cambios }} carguíos
              </b-badge>
            </div>
            <div class="d-flex justify-content-between align-items-baseline mt-1 pt-50 border-top-light">
              <span class="text-muted font-small-3 font-weight-bold">Subtotal:</span>
              <span class="font-weight-bolder text-warning kpi-amount">
                S/ {{ totales.monto_cambios | currency }}
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- 3. Retornos -->
        <b-col v-if="retornos.length > 0" class="mb-1" :md="kpiColSpan" sm="6">
          <b-card no-body class="border shadow-none mb-0 bg-light-secondary p-1 h-100 card-kpi-custom">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bolder text-dark text-uppercase kpi-title">
                <feather-icon icon="RotateCcwIcon" size="16" class="mr-25" /> Retornos
              </span>
              <b-badge variant="secondary" class="font-medium-1 px-1 font-weight-bolder">
                {{ totales.cant_retornos }} retornos
              </b-badge>
            </div>
            <div class="d-flex justify-content-between align-items-baseline mt-1 pt-50 border-top-light">
              <span class="text-muted font-small-3 font-weight-bold">Subtotal:</span>
              <span class="font-weight-bolder text-dark kpi-amount">
                S/ {{ totales.monto_retornos | currency }}
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- 4. Bonos F1 -->
        <b-col v-if="bonosF1.length > 0" class="mb-1" :md="kpiColSpan" sm="6">
          <b-card no-body class="border shadow-none mb-0 bg-light-info p-1 h-100 card-kpi-custom">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bolder text-info text-uppercase kpi-title">
                <feather-icon icon="ZapIcon" size="16" class="mr-25" /> Bonos F1
              </span>
              <b-badge variant="info" class="font-medium-1 px-1 font-weight-bolder">
                {{ totales.cant_f1 }} servicios
              </b-badge>
            </div>
            <div class="d-flex justify-content-between align-items-baseline mt-1 pt-50 border-top-light">
              <span class="text-muted font-small-3 font-weight-bold">Subtotal:</span>
              <span class="font-weight-bolder text-info kpi-amount">
                S/ {{ totales.monto_f1 | currency }}
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- 5. Total Neto a Pagar (Siempre Visible) -->
        <b-col class="mb-1" :md="kpiColSpan" sm="6">
          <b-card no-body class="border shadow-none mb-0 card-total-guinda border-guinda p-1 h-100 card-kpi-custom">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bolder text-guinda text-uppercase kpi-title">
                <feather-icon icon="DollarSignIcon" size="16" class="mr-25" /> Total Neto a Pagar
              </span>
              <b-badge variant="danger" class="font-medium-1 px-1 font-weight-bolder badge-guinda">
                Total Liquidado
              </b-badge>
            </div>
            <div class="d-flex justify-content-between align-items-baseline mt-1 pt-50 border-top-light">
              <span class="text-muted font-small-3 font-weight-bold">Neto final:</span>
              <span class="font-weight-bolder text-guinda kpi-amount-total">
                S/ {{ totales.total_bono_neto | currency }}
              </span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- SECCIÓN 1: VIAJES POR DISTANCIA (SOLO SI TIENE DATOS) -->
      <b-card v-if="viajes.length > 0" no-body class="border mb-2 shadow-none section-card">
        <b-card-header class="bg-light-primary py-75 px-1 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <feather-icon icon="TruckIcon" size="20" class="text-primary mr-50" />
            <h4 class="mb-0 font-weight-bolder text-primary">
              1. Viajes por Distancia ({{ viajes.length }})
            </h4>
          </div>
          <b-badge variant="primary" class="font-medium-1 px-1 py-50 font-weight-bolder">
            Subtotal: S/ {{ totales.monto_viajes | currency }}
          </b-badge>
        </b-card-header>

        <div class="table-responsive">
          <b-table-simple hover small class="mb-0 text-nowrap table-custom">
            <b-thead class="custom-table-head">
              <b-tr>
                <b-th class="text-center" style="width: 45px;">#</b-th>
                <b-th class="text-center" style="width: 130px;">N° VIAJE</b-th>
                <b-th class="text-center" style="width: 110px;">FECHA</b-th>
                <b-th>DESTINO / RUTA</b-th>
                <b-th>CLIENTE</b-th>
                <b-th class="text-center" style="width: 130px;">RANGO (KM)</b-th>
                <b-th class="text-right" style="width: 130px;">IMPORTE</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(v, idx) in viajes" :key="'viaje-' + idx">
                <b-td class="text-center font-weight-bold text-secondary">{{ idx + 1 }}</b-td>
                <b-td class="text-center font-weight-bolder text-primary font-medium-1">
                  {{ v.viaje }}
                </b-td>
                <b-td class="text-center font-weight-bold text-body-dark">
                  {{ v.fecha }}
                </b-td>
                <b-td class="font-weight-bolder text-body-dark">
                  {{ v.ruta }}
                </b-td>
                <b-td class="font-weight-bold text-body-dark text-uppercase">
                  {{ v.cliente }}
                </b-td>
                <b-td class="text-center">
                  <b-badge variant="light-primary" class="font-small-2 font-weight-bolder px-75 py-25">
                    {{ v.rango }}
                  </b-badge>
                </b-td>
                <b-td class="text-right font-weight-bolder text-success font-medium-1">
                  <span v-if="v.monto > 0">S/ {{ v.monto | currency }}</span>
                  <span v-else class="text-muted">S/ -</span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot class="bg-light font-weight-bold">
              <b-tr>
                <b-td colspan="6" class="text-right font-medium-1 text-dark">SUBTOTAL VIAJES POR DISTANCIA:</b-td>
                <b-td class="text-right text-success font-weight-bolder font-medium-2">
                  S/ {{ totales.monto_viajes | currency }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
      </b-card>

      <!-- SECCIÓN 2: CAMBIOS DE CONDUCTOR - CARGUÍOS (SOLO SI TIENE DATOS) -->
      <b-card v-if="cambiosConductor.length > 0" no-body class="border mb-2 shadow-none section-card">
        <b-card-header class="bg-light-warning py-75 px-1 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <feather-icon icon="UsersIcon" size="20" class="text-warning mr-50" />
            <h4 class="mb-0 font-weight-bolder text-warning">
              2. Cambios de Conductor — Carguíos ({{ cambiosConductor.length }})
            </h4>
          </div>
          <b-badge variant="warning" class="font-medium-1 px-1 py-50 font-weight-bolder">
            Subtotal: S/ {{ totales.monto_cambios | currency }}
          </b-badge>
        </b-card-header>

        <div class="table-responsive">
          <b-table-simple hover small class="mb-0 text-nowrap table-custom">
            <b-thead class="custom-table-head">
              <b-tr>
                <b-th class="text-center" style="width: 45px;">#</b-th>
                <b-th class="text-center" style="width: 130px;">N° VIAJE</b-th>
                <b-th class="text-center" style="width: 110px;">FECHA</b-th>
                <b-th>RUTA / TRAMO</b-th>
                <b-th>CLIENTE</b-th>
                <b-th class="text-right" style="width: 130px;">IMPORTE</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(cc, idx) in cambiosConductor" :key="'cc-' + idx">
                <b-td class="text-center font-weight-bold text-secondary">{{ idx + 1 }}</b-td>
                <b-td class="text-center font-weight-bolder text-warning font-medium-1">
                  {{ cc.viaje }}
                </b-td>
                <b-td class="text-center font-weight-bold text-body-dark">
                  {{ cc.fecha }}
                </b-td>
                <b-td class="font-weight-bolder text-body-dark">
                  {{ cc.ruta }}
                </b-td>
                <b-td class="font-weight-bold text-body-dark text-uppercase">
                  {{ cc.cliente }}
                </b-td>
                <b-td class="text-right font-weight-bolder text-warning font-medium-1">
                  <span v-if="cc.monto > 0">S/ {{ cc.monto | currency }}</span>
                  <span v-else class="text-muted">S/ -</span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot class="bg-light font-weight-bold">
              <b-tr>
                <b-td colspan="5" class="text-right font-medium-1 text-dark">SUBTOTAL CAMBIOS CONDUCTOR (CARGUÍOS):</b-td>
                <b-td class="text-right text-warning font-weight-bolder font-medium-2">
                  S/ {{ totales.monto_cambios | currency }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
      </b-card>

      <!-- SECCIÓN 3: RETORNOS (SOLO SI TIENE DATOS) -->
      <b-card v-if="retornos.length > 0" no-body class="border mb-2 shadow-none section-card">
        <b-card-header class="bg-light-secondary py-75 px-1 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <feather-icon icon="RotateCcwIcon" size="20" class="text-secondary mr-50" />
            <h4 class="mb-0 font-weight-bolder text-secondary">
              3. Retornos ({{ retornos.length }})
            </h4>
          </div>
          <b-badge variant="secondary" class="font-medium-1 px-1 py-50 font-weight-bolder">
            Subtotal: S/ {{ totales.monto_retornos | currency }}
          </b-badge>
        </b-card-header>

        <div class="table-responsive">
          <b-table-simple hover small class="mb-0 text-nowrap table-custom">
            <b-thead class="custom-table-head">
              <b-tr>
                <b-th class="text-center" style="width: 45px;">#</b-th>
                <b-th class="text-center" style="width: 130px;">N° VIAJE</b-th>
                <b-th class="text-center" style="width: 110px;">FECHA</b-th>
                <b-th>RUTA</b-th>
                <b-th>CLIENTE</b-th>
                <b-th class="text-right" style="width: 130px;">IMPORTE</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(ret, idx) in retornos" :key="'ret-' + idx">
                <b-td class="text-center font-weight-bold text-secondary">{{ idx + 1 }}</b-td>
                <b-td class="text-center font-weight-bolder text-secondary font-medium-1">
                  {{ ret.viaje }}
                </b-td>
                <b-td class="text-center font-weight-bold text-body-dark">
                  {{ ret.fecha }}
                </b-td>
                <b-td class="font-weight-bolder text-body-dark">
                  {{ ret.ruta }}
                </b-td>
                <b-td class="font-weight-bold text-body-dark text-uppercase">
                  {{ ret.cliente }}
                </b-td>
                <b-td class="text-right font-weight-bolder text-secondary font-medium-1">
                  <span v-if="ret.monto > 0">S/ {{ ret.monto | currency }}</span>
                  <span v-else class="text-muted">S/ -</span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot class="bg-light font-weight-bold">
              <b-tr>
                <b-td colspan="5" class="text-right font-medium-1 text-dark">SUBTOTAL RETORNOS:</b-td>
                <b-td class="text-right text-secondary font-weight-bolder font-medium-2">
                  S/ {{ totales.monto_retornos | currency }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
      </b-card>

      <!-- SECCIÓN 4: BONOS F1 (SOLO SI TIENE DATOS) -->
      <b-card v-if="bonosF1.length > 0" no-body class="border mb-2 shadow-none section-card">
        <b-card-header class="bg-light-info py-75 px-1 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <feather-icon icon="ZapIcon" size="20" class="text-info mr-50" />
            <h4 class="mb-0 font-weight-bolder text-info">
              4. Bonos F1 ({{ bonosF1.length }})
            </h4>
          </div>
          <b-badge variant="info" class="font-medium-1 px-1 py-50 font-weight-bolder">
            Subtotal: S/ {{ totales.monto_f1 | currency }}
          </b-badge>
        </b-card-header>

        <div class="table-responsive">
          <b-table-simple hover small class="mb-0 text-nowrap table-custom">
            <b-thead class="custom-table-head">
              <b-tr>
                <b-th class="text-center" style="width: 45px;">#</b-th>
                <b-th class="text-center" style="width: 130px;">N° VIAJE</b-th>
                <b-th class="text-center" style="width: 110px;">FECHA</b-th>
                <b-th>RUTA</b-th>
                <b-th>CLIENTE</b-th>
                <b-th class="text-right" style="width: 130px;">IMPORTE</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(f, idx) in bonosF1" :key="'f1-' + idx">
                <b-td class="text-center font-weight-bold text-secondary">{{ idx + 1 }}</b-td>
                <b-td class="text-center font-weight-bolder text-info font-medium-1">{{ f.viaje }}</b-td>
                <b-td class="text-center font-weight-bold text-body-dark">{{ f.fecha }}</b-td>
                <b-td class="font-weight-bolder text-body-dark">{{ f.ruta }}</b-td>
                <b-td class="font-weight-bold text-body-dark text-uppercase">{{ f.cliente }}</b-td>
                <b-td class="text-right font-weight-bolder text-info font-medium-1">
                  <span v-if="f.monto > 0">S/ {{ f.monto | currency }}</span>
                  <span v-else class="text-muted">S/ -</span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot class="bg-light font-weight-bold">
              <b-tr>
                <b-td colspan="5" class="text-right font-medium-1 text-dark">SUBTOTAL BONOS F1:</b-td>
                <b-td class="text-right text-info font-weight-bolder font-medium-2">
                  S/ {{ totales.monto_f1 | currency }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
      </b-card>

      <!-- BARRA DE TOTALES INFERIOR RESUMIDA -->
      <div class="p-1 rounded bg-light border d-flex flex-wrap justify-content-between align-items-center mt-2 mb-2 summary-footer-bar">
        <div class="d-flex flex-wrap align-items-center gap-x-3 font-medium-1 text-dark">
          <span v-if="totales.monto_viajes > 0" class="mr-2">
            Viajes: <strong class="text-success font-weight-bolder">S/ {{ totales.monto_viajes | currency }}</strong>
          </span>
          <span v-if="totales.monto_cambios > 0" class="mr-2">
            Carguíos: <strong class="text-warning font-weight-bolder">S/ {{ totales.monto_cambios | currency }}</strong>
          </span>
          <span v-if="totales.monto_retornos > 0" class="mr-2">
            Retornos: <strong class="text-secondary font-weight-bolder">S/ {{ totales.monto_retornos | currency }}</strong>
          </span>
          <span v-if="totales.monto_f1 > 0" class="mr-2">
            F1: <strong class="text-info font-weight-bolder">S/ {{ totales.monto_f1 | currency }}</strong>
          </span>
          <span v-if="totales.descuentos > 0" class="mr-2">
            Descuentos: <strong class="text-danger font-weight-bolder">- S/ {{ totales.descuentos | currency }}</strong>
          </span>
        </div>
        <div class="mt-50 mt-md-0">
          <span class="font-large-1 font-weight-bolder text-guinda">
            Total a Cobrar: S/ {{ totales.total_bono_neto | currency }}
          </span>
        </div>
      </div>

      <!-- BOTONES DE ACCIÓN INFERIORES -->
      <div class="d-flex justify-content-end align-items-center gap-x-2 pt-1 border-top">
        <b-button
          variant="outline-primary"
          @click="descargarPdf"
          :disabled="downloadingPdf"
          class="font-weight-bold"
        >
          <b-spinner small v-if="downloadingPdf" class="mr-50" />
          <feather-icon icon="DownloadIcon" class="mr-50" v-else />
          Descargar PDF
        </b-button>
        <b-button variant="primary" @click="$emit('close')" class="font-weight-bold px-2">
          Cerrar
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import BonoService from "@/views/rrhh/views/payroll/components/bonos/service/bono.service.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "BonoResumenConductorModal",
  props: {
    modalData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      show: true,
      loading: false,
      downloadingPdf: false,
      conductor: {},
      totales: {},
      viajes: [],
      cambiosConductor: [],
      retornos: [],
      bonosF1: [],
      devoluciones: [],
    };
  },
  computed: {
    // Calcula cuántas columnas dar a cada tarjeta KPI activa
    kpiColSpan() {
      let activas = 1; // Total neto siempre está
      if (this.viajes.length > 0) activas++;
      if (this.cambiosConductor.length > 0) activas++;
      if (this.retornos.length > 0) activas++;
      if (this.bonosF1.length > 0) activas++;

      if (activas <= 2) return 6;
      if (activas === 3) return 4;
      if (activas === 4) return 3;
      return 2; // 5 o más
    },
  },
  async mounted() {
    await this.cargarResumen();
  },
  methods: {
    async cargarResumen() {
      try {
        this.loading = true;
        const params = {
          bono_id: this.modalData.id,
          employee_id: this.modalData.employee_id,
          bono_periodo_id: this.modalData.bono_periodo_id,
        };
        const { data } = await BonoService.getResumenBono(params);
        if (data.success && data.data) {
          this.conductor = data.data.conductor || {};
          this.totales = data.data.totales || {};
          this.viajes = data.data.viajes || [];
          this.cambiosConductor = data.data.cambios_conductor || [];
          this.retornos = data.data.retornos || [];
          this.bonosF1 = data.data.bonos_f1 || [];
          this.devoluciones = data.data.devoluciones || [];
        }
      } catch (error) {
        console.error("Error cargando resumen de bono:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: "No se pudo cargar el resumen del bono.",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async descargarPdf() {
      try {
        this.downloadingPdf = true;
        const params = {
          employee_id: this.modalData.employee_id,
          bono_periodo_id: this.modalData.bono_periodo_id,
        };
        const response = await BonoService.generarPdfBono(params);
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `RESUMEN_BONO_${this.conductor.nombre_completo || "CONDUCTOR"}_${this.conductor.periodo || "PERIODO"}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Éxito",
              icon: "CheckCircleIcon",
              variant: "success",
              text: "PDF descargado correctamente.",
            },
          });
        }
      } catch (error) {
        console.error("Error descargando PDF:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: "No se pudo generar el archivo PDF.",
          },
        });
      } finally {
        this.downloadingPdf = false;
      }
    },
  },
};
</script>

<style scoped>
/* Nombre del conductor en la cabecera */
.driver-name-title {
  font-size: 1.65rem !important;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

/* Tarjetas KPI Superiores */
.card-kpi-custom {
  border-radius: 8px !important;
  border-width: 1.5px !important;
}

.kpi-title {
  font-size: 0.92rem !important;
  letter-spacing: 0.5px;
}

.kpi-amount {
  font-size: 1.6rem !important;
  line-height: 1;
}

.kpi-amount-total {
  font-size: 1.75rem !important;
  line-height: 1;
}

.border-top-light {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-resumen-bono .border-guinda {
  border: 1.5px solid #f3ccd5 !important;
}

.card-total-guinda {
  background-color: #fdf2f4 !important;
}

.text-guinda {
  color: #72122c !important;
}

.badge-guinda {
  background-color: #72122c !important;
}

/* Tablas de Detalle */
.section-card {
  border-radius: 8px !important;
  overflow: hidden;
}

.text-body-dark {
  color: #1e293b !important;
}

.custom-table-head th {
  background-color: #eef2f6 !important;
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #cbd5e1 !important;
  vertical-align: middle !important;
  padding-top: 0.65rem !important;
  padding-bottom: 0.65rem !important;
}

.table-custom td {
  vertical-align: middle !important;
  padding-top: 0.65rem !important;
  padding-bottom: 0.65rem !important;
}

.summary-footer-bar {
  border-radius: 8px !important;
  border: 1.5px solid #e2e8f0 !important;
}

.gap-x-2 {
  column-gap: 0.5rem;
}

.gap-x-3 {
  column-gap: 1.25rem;
}

.gap-y-1 {
  row-gap: 0.35rem;
}

/* Modo oscuro */
.dark-layout .driver-name-title {
  color: #7367f0 !important;
}

.dark-layout .text-body-dark {
  color: #e2e8f0 !important;
}

.dark-layout .custom-table-head th {
  background-color: #283046 !important;
  color: #d0d2d6 !important;
  border-bottom-color: #3b4253 !important;
}

.dark-layout .card-total-guinda {
  background-color: #3b171f !important;
}

.dark-layout .border-guinda {
  border-color: #5c2432 !important;
}

.dark-layout .text-guinda {
  color: #fecdd3 !important;
}
</style>
